import React, { useState, useEffect } from 'react';

import CustomerHeader from 'components/ui/CustomerHeader';
import SearchForm from './components/SearchForm';
import FilterSidebar from './components/FilterSidebar';
import ResultsGrid from './components/ResultsGrid';
import SortControls from './components/SortControls';
import Icon from 'components/AppIcon';
import googleFlightsService from 'utils/googleFlightsService';

const SearchResultsDashboard = () => {
  const [searchParams, setSearchParams] = useState({
    tripType: 'round-trip',
    origin: '',
    destination: '',
    departDate: '',
    returnDate: '',
    passengers: { adults: 1, children: 0, infants: 0 },
    rooms: 1,
    services: { flights: true, hotels: true, cars: true },
    cabinClass: 'economy'
  });

  const [filters, setFilters] = useState({
    priceRange: [0, 5000],
    airlines: [],
    hotelStars: [],
    carTypes: [],
    layovers: 'any',
    departureTime: 'any',
    amenities: []
  });

  const [sortBy, setSortBy] = useState('price');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [searchError, setSearchError] = useState(null);

  // Search flights using Google Flights API
  const searchFlights = async (params) => {
    try {
      setIsLoading(true);
      setSearchError(null);
      
      const flightResults = await googleFlightsService?.searchFlights(params);
      
      // Combine with mock hotel and car data for complete travel packages
      const enhancedResults = flightResults?.map((flight, index) => ({
        ...flight,
        type: 'combo',
        totalPrice: flight?.flight?.price + (index % 3 === 0 ? 567 : index % 3 === 1 ? 910 : 401), // Add hotel + car prices
        hotel: getMockHotelData(index),
        car: getMockCarData(index)
      }));

      setResults(enhancedResults);
    } catch (error) {
      console.error('Search error:', error);
      setSearchError('Failed to search flights. Please try again.');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Mock hotel data helper
  const getMockHotelData = (index) => {
    const hotels = [
      {
        name: 'Grand Plaza Hotel',
        rating: 4.5,
        stars: 4,
        location: 'Downtown',
        amenities: ['WiFi', 'Pool', 'Gym', 'Spa'],
        roomType: 'Deluxe King Room',
        price: 189,
        nights: 3
      },
      {
        name: 'Luxury Suites',
        rating: 4.8,
        stars: 5,
        location: 'City Center',
        amenities: ['WiFi', 'Pool', 'Gym', 'Spa', 'Concierge'],
        roomType: 'Executive Suite',
        price: 299,
        nights: 3
      },
      {
        name: 'Budget Inn',
        rating: 3.8,
        stars: 3,
        location: 'Airport Area',
        amenities: ['WiFi', 'Parking'],
        roomType: 'Standard Room',
        price: 89,
        nights: 3
      }
    ];
    return hotels?.[index % hotels?.length];
  };

  // Mock car data helper
  const getMockCarData = (index) => {
    const cars = [
      {
        company: 'Enterprise',
        model: 'Toyota Camry or similar',
        type: 'Intermediate',
        features: ['Automatic', 'AC', 'GPS'],
        price: 84,
        days: 3
      },
      {
        company: 'Hertz',
        model: 'BMW 3 Series or similar',
        type: 'Luxury',
        features: ['Automatic', 'AC', 'GPS', 'Leather'],
        price: 156,
        days: 3
      },
      {
        company: 'Budget',
        model: 'Nissan Versa or similar',
        type: 'Economy',
        features: ['Automatic', 'AC'],
        price: 45,
        days: 3
      }
    ];
    return cars?.[index % cars?.length];
  };

  useEffect(() => {
    // Trigger search when both origin and destination are provided
    if (searchParams?.origin && searchParams?.destination && searchParams?.departDate) {
      searchFlights(searchParams);
    }
  }, [searchParams]);

  const handleSearch = (newSearchParams) => {
    setSearchParams(newSearchParams);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
  };

  const filteredAndSortedResults = results?.filter(result => {
      // Apply filters
      if (filters?.priceRange?.[0] > 0 && result?.totalPrice < filters?.priceRange?.[0]) return false;
      if (filters?.priceRange?.[1] < 5000 && result?.totalPrice > filters?.priceRange?.[1]) return false;
      if (filters?.hotelStars?.length > 0 && !filters?.hotelStars?.includes(result?.hotel?.stars)) return false;
      if (filters?.airlines?.length > 0 && !filters?.airlines?.some(airline => 
        result?.flight?.airline?.toLowerCase()?.includes(airline?.toLowerCase())
      )) return false;
      if (filters?.layovers !== 'any') {
        const maxStops = parseInt(filters?.layovers);
        if (result?.flight?.stops > maxStops) return false;
      }
      return true;
    })?.sort((a, b) => {
      switch (sortBy) {
        case 'price':
          return a?.totalPrice - b?.totalPrice;
        case 'duration':
          return a?.flight?.duration?.localeCompare(b?.flight?.duration);
        case 'eco-score':
          return a?.carbonFootprint - b?.carbonFootprint;
        case 'rating':
          return b?.hotel?.rating - a?.hotel?.rating;
        case 'departure':
          return a?.flight?.departure?.time?.localeCompare(b?.flight?.departure?.time);
        default:
          return 0;
      }
    });

  return (
    <div className="min-h-screen bg-background">
      <CustomerHeader />
      <main className="pt-16">
        {/* Search Form Section */}
        <div className="bg-surface border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <SearchForm 
              searchParams={searchParams}
              onSearch={handleSearch}
              isLoading={isLoading}
            />
            
            {/* Search Error Display */}
            {searchError && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <Icon name="AlertCircle" size={20} className="text-red-600 mr-2" />
                  <span className="text-red-800 font-body-medium">{searchError}</span>
                </div>
              </div>
            )}

            {/* API Status Indicator */}
            {!import.meta.env?.VITE_GOOGLE_FLIGHTS_API_KEY && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center">
                  <Icon name="Info" size={20} className="text-yellow-600 mr-2" />
                  <span className="text-yellow-800 font-body-medium">
                    Google Flights API key not configured. Using enhanced mock data for demonstration.
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Results Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Mobile Filter Toggle */}
            <div className="lg:hidden">
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="flex items-center justify-center w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-secondary hover:text-primary transition-colors duration-200 ease-out"
              >
                <Icon name="Filter" size={20} className="mr-2" />
                Filters & Sort
                <Icon name="ChevronDown" size={16} className="ml-2" />
              </button>
            </div>

            {/* Filter Sidebar */}
            <div className={`lg:block ${isFilterOpen ? 'block' : 'hidden'} lg:w-80`}>
              <FilterSidebar
                filters={filters}
                onFilterChange={handleFilterChange}
                resultCount={filteredAndSortedResults?.length}
              />
            </div>

            {/* Results Content */}
            <div className="flex-1">
              {/* Sort Controls */}
              <SortControls
                sortBy={sortBy}
                onSortChange={handleSortChange}
                resultCount={filteredAndSortedResults?.length}
                isLoading={isLoading}
              />

              {/* Results Grid */}
              <ResultsGrid
                results={filteredAndSortedResults}
                isLoading={isLoading}
                searchParams={searchParams}
              />

              {/* Load More Button */}
              {!isLoading && filteredAndSortedResults?.length > 0 && (
                <div className="mt-8 text-center">
                  <button 
                    onClick={() => searchFlights(searchParams)}
                    className="px-6 py-3 bg-primary text-white font-body-medium rounded-lg hover:bg-primary-700 transition-all duration-200 ease-out"
                  >
                    Refresh Search Results
                  </button>
                </div>
              )}

              {/* No Results Message */}
              {!isLoading && filteredAndSortedResults?.length === 0 && searchParams?.origin && searchParams?.destination && (
                <div className="text-center py-12">
                  <Icon name="Plane" size={48} className="mx-auto text-text-secondary mb-4" />
                  <h3 className="text-xl font-body-semibold text-text-primary mb-2">No flights found</h3>
                  <p className="text-text-secondary mb-4">
                    Try adjusting your search criteria or dates for more options.
                  </p>
                  <button
                    onClick={() => setFilters({
                      priceRange: [0, 5000],
                      airlines: [],
                      hotelStars: [],
                      carTypes: [],
                      layovers: 'any',
                      departureTime: 'any',
                      amenities: []
                    })}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 ease-out"
                  >
                    Clear Filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SearchResultsDashboard;